/* ==========================================================================
   LAUZON — product page behaviour
   Ported from the prototype main.js PDP pieces: thumbnail swap with GSAP
   crossfade, gallery arrows cycling the thumb set, the sticky mini
   add-to-cart bar, and the per-item video reveal. Idempotent; re-runs on
   shopify:section:load for the theme editor.

   And click-to-expand (Dave S., 2026-08-31): the main frame opens a
   full-screen lightbox of the same photo set, at whichever photo the gallery
   is currently showing.

   🚫 NO HOVER-TO-ZOOM (Dave S., 2026-09-16, Dave L.'s call). The main frame
   used to magnify under the cursor. Dave L. found the cursor-tracked motion
   nauseating and it earns nothing the lightbox doesn't: the photography is
   close-up enough that opening the full set at screen size gives the same
   read of grain and hardware, without a photo that moves while you look at
   it. The clipping frame, data-zoom-src and the .gal-zoom class name stay —
   they are the lightbox's markup now.
   ========================================================================== */

(function () {
  'use strict';

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---- Click-to-expand lightbox -----------------------------------------
     ONE overlay for the page, built on first open and reused. Building it per
     gallery would be wasted markup on a page that has exactly one, and
     building it up front costs every PDP a DOM subtree most readers never
     open.

     The photo list comes from the THUMBNAILS, not from the main <img>: the
     thumbs are the canonical ordered set (the main frame is a single element
     the thumbs swap into), and they already carry the alt text and the
     high-resolution source. A product with one photo has no thumb rail, so
     that case falls back to the frame's own data-zoom-src.

     Deliberately not a <dialog>: showModal() gives a free focus trap and free
     inert-background, but its ::backdrop cannot be transitioned consistently
     across the browsers this store sees, and Safari still mis-handles a
     scroll lock underneath it. The trap below is small and behaves the same
     everywhere. */
  var lightbox = null;

  function buildLightbox() {
    if (lightbox) return lightbox;

    var el = document.createElement('div');
    el.className = 'lz-lightbox';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Product photo');
    el.hidden = true;
    el.innerHTML =
      '<figure class="lz-lightbox-figure">' +
        '<img class="lz-lightbox-img" alt="">' +
      '</figure>' +
      '<button class="lz-lightbox-btn lz-lightbox-close" type="button" data-lb-close aria-label="Close">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      '</button>' +
      '<button class="lz-lightbox-btn lz-lightbox-prev" type="button" data-lb-prev aria-label="Previous photo">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 6 9 12l5.5 6"/></svg>' +
      '</button>' +
      '<button class="lz-lightbox-btn lz-lightbox-next" type="button" data-lb-next aria-label="Next photo">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9.5 6 15 12l-5.5 6"/></svg>' +
      '</button>' +
      '<p class="lz-lightbox-count" data-lb-count aria-live="polite"></p>';

    document.body.appendChild(el);

    lightbox = {
      el: el,
      img: el.querySelector('.lz-lightbox-img'),
      count: el.querySelector('[data-lb-count]'),
      prev: el.querySelector('[data-lb-prev]'),
      next: el.querySelector('[data-lb-next]'),
      close: el.querySelector('.lz-lightbox-close'),
      items: [],
      index: 0,
      opener: null,
      scrollY: 0
    };

    el.addEventListener('click', function (event) {
      if (event.target.closest('[data-lb-close]')) { closeLightbox(); return; }
      if (event.target.closest('[data-lb-prev]')) { stepLightbox(-1); return; }
      if (event.target.closest('[data-lb-next]')) { stepLightbox(1); return; }
      /* Click outside the photo closes. The backdrop is `.lz-lightbox::before`
         rather than an element, so "outside" is a hit on the container itself
         — a pseudo-element is not an event target and the click lands here.

         🔴 The backdrop STARTED as an empty <div> and rendered nothing:
         Dawn's base.css carries `div:empty { display: none }`, which at 0,1,1
         beats a single class. A pseudo-element sidesteps that whole family of
         rules instead of out-specifying one of them. */
      if (event.target === el) closeLightbox();
    });

    /* Keydown on the overlay, not on document: the listener dies with the
       overlay's own focus, so nothing is left listening on a closed lightbox
       and Escape keeps working for anything else on the page. */
    el.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') { event.preventDefault(); closeLightbox(); return; }
      if (event.key === 'ArrowLeft') { event.preventDefault(); stepLightbox(-1); return; }
      if (event.key === 'ArrowRight') { event.preventDefault(); stepLightbox(1); return; }
      if (event.key !== 'Tab') return;

      // Focus trap. The overlay's buttons are the only focusable things in it.
      var focusable = Array.prototype.filter.call(
        el.querySelectorAll('button'),
        function (b) { return b.offsetParent !== null; }
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    return lightbox;
  }

  function renderLightbox() {
    var lb = lightbox;
    var item = lb.items[lb.index];
    if (!item) return;
    lb.img.setAttribute('src', item.src);
    lb.img.setAttribute('alt', item.alt || '');
    var many = lb.items.length > 1;
    lb.prev.hidden = !many;
    lb.next.hidden = !many;
    lb.count.textContent = many ? (lb.index + 1) + ' / ' + lb.items.length : '';
  }

  function stepLightbox(dir) {
    var lb = lightbox;
    if (!lb || lb.items.length < 2) return;
    lb.index = (lb.index + dir + lb.items.length) % lb.items.length;
    renderLightbox();
  }

  function closeLightbox() {
    var lb = lightbox;
    if (!lb || lb.el.hidden) return;
    lb.el.hidden = true;
    document.documentElement.classList.remove('lz-lightbox-open');
    document.body.style.top = '';
    document.body.style.position = '';
    document.body.style.width = '';
    window.scrollTo(0, lb.scrollY);
    /* preventScroll, and it is not belt-and-braces — without it the whole
       scroll restore above is undone. Returning focus to the frame is correct
       behaviour, but focus() scrolls its target into view by default, so the
       page landed at the gallery instead of where the reader opened from.
       Measured: opened at y1400, closed at y186 every time. */
    if (lb.opener && typeof lb.opener.focus === 'function') {
      lb.opener.focus({ preventScroll: true });
    }
    lb.opener = null;
  }

  function openLightbox(items, index, opener) {
    if (!items.length) return;
    var lb = buildLightbox();
    lb.items = items;
    lb.index = Math.max(0, Math.min(index, items.length - 1));
    lb.opener = opener || null;

    /* Scroll lock that survives iOS: `overflow: hidden` alone does not hold
       there, so the body is pinned at a negative offset and restored on close.
       The offset has to be read BEFORE the class lands or it reads 0. */
    lb.scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = '-' + lb.scrollY + 'px';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.documentElement.classList.add('lz-lightbox-open');

    renderLightbox();
    lb.el.hidden = false;
    lb.close.focus();
  }

  /* The photo set, read fresh at open time rather than cached at init: the
     theme editor can re-render the section under us, and a stale list would
     open the previous product's photographs. */
  function galleryItems(gallery) {
    var thumbs = gallery.querySelectorAll('.thumb[data-zoom-src]');
    if (thumbs.length) {
      return Array.prototype.map.call(thumbs, function (t) {
        var img = t.querySelector('img');
        return {
          src: t.getAttribute('data-zoom-src'),
          alt: img ? img.getAttribute('alt') : ''
        };
      });
    }
    var frame = gallery.querySelector('[data-gal-open]');
    if (!frame) return [];
    var single = frame.querySelector('img');
    return [{
      src: frame.getAttribute('data-zoom-src') || (single && single.getAttribute('src')),
      alt: single ? single.getAttribute('alt') : ''
    }];
  }

  function initExpand(gallery) {
    var frame = gallery.querySelector('[data-gal-open]');
    if (!frame || frame.dataset.expandInit) return;
    frame.dataset.expandInit = '1';

    var open = function () {
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('.thumb[data-zoom-src]'));
      var index = thumbs.findIndex(function (t) {
        return t.getAttribute('aria-pressed') === 'true';
      });
      openLightbox(galleryItems(gallery), index < 0 ? 0 : index, frame);
    };

    frame.addEventListener('click', open);
    frame.addEventListener('keydown', function (event) {
      /* Enter and Space, because role="button" gets neither for free — an
         element with a button ROLE still has a div's key behaviour. */
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      open();
    });
  }

  function init(root) {
    var gallery = root.querySelector('.pdp-gallery');
    if (!gallery || gallery.dataset.lauzonInit) return;
    gallery.dataset.lauzonInit = '1';

    initExpand(gallery);

    var mainImg = gallery.querySelector('.gallery-main img');
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('.thumb[data-full]'));

    /* ------------------------------------------------------------------
       THE THUMBNAIL RAIL'S COUNTER (design pass P1, approved by Dave S. on a
       rendered mock 2026-09-09). The 29-thumbnail wall became one horizontal
       strip, which trades a problem for a smaller one: a rail shows you eight
       of twenty-nine and says nothing about the other twenty-one. The counter
       says how many there are and where you are; the arrows below move it.

       Built here rather than in sections/lauzon-product.liquid on purpose.
       Everything it reports — which thumb is pressed, whether the rail
       overflows, whether it has been scrolled — is only knowable at runtime,
       so a Liquid-rendered row would be markup that is wrong until this file
       corrects it. Styles: PART 6 of assets/lauzon-product-main.css.

       aria-hidden, and not a live region: every thumb is already a button
       labelled "Photo 3 of 29", so a screen reader is told the position by
       the control it is on. Announcing it a second time from a paragraph
       nobody focused would be duplication, not access.
       ------------------------------------------------------------------ */
    var rail = gallery.querySelector('.gallery-thumbs');
    var countCurrent = null;
    var railWrap = null;
    var railPrev = null;
    var railNext = null;
    var syncArrows = function () {};

    /* ------------------------------------------------------------------
       THE RAIL'S ARROWS (Dave S., 2026-09-09, on the built rail).

       The ruling: the strip is not intuitive on desktop — "you cannot really
       scroll it with a mouse". True, and it is a pointer problem, not a
       styling one. A trackpad can flick sideways; a mouse wheel scrolls the
       page, and a click-drag on a native scroller selects rather than pans.
       So the rail became a traditional carousel: an arrow either side that
       pages it, which is the affordance a desktop visitor already reads as
       "there are more of these". The "Scroll for more" hint is gone with
       them — an arrow says it better than a sentence does. Phones are
       untouched: everything here is display:none below 561px and a swipe
       needs no chrome.

       ⚠️ Built here, not in sections/lauzon-product.liquid, for the same two
       reasons the counter row is: the section file is carrying another
       session's uncommitted work in a shared working tree, and the arrows'
       whole state (present at all, enabled, disabled) is a runtime fact
       about whether the rail overflows at this width.

       ⚠️ NOT the site-wide `data-rail-prev` delegation in
       lauzon-global.js, and the reason is the paging amount. `stepRail`
       pages a whole clientWidth, which is right for a card rail and wrong
       here: the rail's width is not a whole number of 88px thumbs, so a
       full-width page lands mid-thumb and mandatory snap corrects it in
       whichever direction is nearer — sometimes skipping the photo that was
       half-visible at the edge. Paging to a thumb's OWN snap position, one
       thumb short of a full width, cannot skip one and cannot fight the snap
       engine (same reasoning as revealThumb below). The trade is that
       stepRail's wrap-around does not come with it, so these disable at the
       ends instead — which on a linear set of photographs is the more honest
       signal anyway: a disabled arrow is how the visitor learns there is no
       photo 30.
       ------------------------------------------------------------------ */
    var ARROW_SVG = {
      prev: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 6 9 12l5.5 6"/></svg>',
      next: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9.5 6 15 12l-5.5 6"/></svg>'
    };

    /* Every thumb's own scroll offset inside the rail, measured from rects
       rather than offsetLeft — offsetLeft is relative to an offsetParent
       that is not this rail. Recomputed per call: cheap for 29 items, and
       correct after a resize or a late-loading image without a cache to
       invalidate. */
    function snapPoints() {
      var railBox = rail.getBoundingClientRect();
      return Array.prototype.map.call(rail.children, function (el) {
        return rail.scrollLeft + (el.getBoundingClientRect().left - railBox.left) - rail.clientLeft;
      });
    }

    function railMax() {
      return Math.max(0, rail.scrollWidth - rail.clientWidth);
    }

    function pageRail(dir) {
      var points = snapPoints();
      if (points.length < 2) return;
      var pitch = points[1] - points[0];
      var max = railMax();
      /* One thumb short of a full width, so the last thumb visible before
         the step is the first one visible after it — nothing crosses the
         edge unseen. Never less than one thumb, in case a narrow rail is
         barely wider than a single thumb. */
      var amount = Math.max(pitch, rail.clientWidth - pitch);
      var target = rail.scrollLeft + dir * amount;
      var best = null;
      points.forEach(function (left) {
        if (left > max) left = max;
        // Guarantee progress: only consider snap points past where we are.
        if (dir > 0 && left <= rail.scrollLeft + 1) return;
        if (dir < 0 && left >= rail.scrollLeft - 1) return;
        if (best === null || Math.abs(left - target) < Math.abs(best - target)) best = left;
      });
      // Past the last snap point there is still a run of rail to the right.
      if (dir > 0 && (best === null || best > max)) best = max;
      if (dir < 0 && best === null) best = 0;
      rail.scrollTo({ left: Math.max(0, Math.min(max, best)), behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    function buildArrows() {
      if (!rail || thumbs.length < 2 || rail.parentNode.classList.contains('gallery-railwrap')) return;

      railWrap = document.createElement('div');
      railWrap.className = 'gallery-railwrap';
      rail.parentNode.insertBefore(railWrap, rail);

      railPrev = document.createElement('button');
      railPrev.type = 'button';
      railPrev.className = 'rail-btn';
      railPrev.setAttribute('aria-label', 'Previous photos');
      railPrev.innerHTML = ARROW_SVG.prev;

      railNext = document.createElement('button');
      railNext.type = 'button';
      railNext.className = 'rail-btn';
      railNext.setAttribute('aria-label', 'Next photos');
      railNext.innerHTML = ARROW_SVG.next;

      railWrap.appendChild(railPrev);
      railWrap.appendChild(rail);
      railWrap.appendChild(railNext);

      railPrev.addEventListener('click', function () { pageRail(-1); });
      railNext.addEventListener('click', function () { pageRail(1); });

      /* Present at all / enabled / disabled, in one place. `data-fits` takes
         the wrapper back to display:contents so a product whose photos all
         fit shows no arrows and no gap where they were — the accessory
         template with one photo has no rail to begin with and is untouched.
         +2 of tolerance throughout for sub-pixel widths and a fractional
         scrollLeft on hi-dpi displays, the same reason railAtEnd in
         lauzon-global.js carries one. */
      syncArrows = function () {
        var max = railMax();
        if (max <= 2) {
          railWrap.setAttribute('data-fits', '');
          return;
        }
        railWrap.removeAttribute('data-fits');
        railPrev.disabled = rail.scrollLeft <= 2;
        railNext.disabled = rail.scrollLeft >= max - 2;
      };
      syncArrows();
      rail.addEventListener('scroll', syncArrows, { passive: true });
      window.addEventListener('resize', syncArrows);

      /* ---- Vertical wheel over the rail scrolls it sideways ----
         The missing mouse gesture, and the other half of Dave's note. Only
         on a real pointer: a touch device generates no wheel events anyway,
         but the guard keeps the intent explicit and costs one matchMedia
         read. Deliberately NOT trapping the page: at either end the handler
         returns without preventDefault, so a wheel that runs out of rail
         carries on scrolling the document rather than deadlocking on a
         88px strip. A horizontal-dominant gesture (a trackpad flick) is
         left to the browser, which already does it natively. */
      var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      rail.addEventListener('wheel', function (event) {
        if (!finePointer.matches || event.ctrlKey) return;
        if (Math.abs(event.deltaX) >= Math.abs(event.deltaY) || !event.deltaY) return;
        var max = railMax();
        if (max <= 2) return;
        if (event.deltaY < 0 && rail.scrollLeft <= 0) return;
        if (event.deltaY > 0 && rail.scrollLeft >= max) return;
        event.preventDefault();
        rail.scrollLeft = Math.max(0, Math.min(max, rail.scrollLeft + event.deltaY));
      }, { passive: false });
    }

    function buildCounter() {
      /* Two or more: "1 / 1" under a rail holding a single thumbnail is a
         label for a fact nobody was in doubt about. One-photo accessories
         get no row at all. */
      if (!rail || thumbs.length < 2 || rail.querySelector('.gallery-count')) return;

      var row = document.createElement('div');
      row.className = 'gallery-count';
      row.setAttribute('aria-hidden', 'true');

      var index = document.createElement('p');
      index.className = 'gallery-count__index';
      countCurrent = document.createElement('span');
      countCurrent.setAttribute('data-gal-current', '');
      countCurrent.textContent = '1';
      index.appendChild(countCurrent);
      index.appendChild(document.createTextNode(' / ' + thumbs.length));

      row.appendChild(index);
      /* After the arrow wrapper when there is one, so the counter stays a
         full-width row under the whole carousel rather than becoming a
         fourth grid cell inside it. */
      var after = railWrap || rail;
      after.parentNode.insertBefore(row, after.nextSibling);
    }

    function updateCounter(thumb) {
      if (!countCurrent) return;
      var at = thumbs.indexOf(thumb);
      if (at >= 0) countCurrent.textContent = String(at + 1);
    }

    /* Only the arrows and the keyboard call this. A click ON a thumb is
       already a deliberate act on something the visitor can see, and pulling
       the rail under their finger afterwards would move the thing they just
       hit.

       ⚠️ NOT scrollIntoView({inline: 'nearest'}), WHICH LOSES A FIGHT WITH
       MANDATORY SNAP. Measured 2026-09-09 stepping to thumb 13 of 29: the
       minimum scroll that reveals it is 534, the browser performs it, and
       then the snap engine pulls the rail back to the nearest snap point at
       488 — leaving 46px of the thumb it had just been asked to reveal
       clipped off the right edge. 'nearest' asks for a position that is not
       a snap point, so on a mandatory rail it is asking to be overruled.

       Scrolling to the thumb's OWN snap position instead means the snap
       engine has nothing to correct: every thumb is scroll-snap-align: start,
       so this lands exactly on a snap point and stays there. Measured from
       rects rather than offsetLeft, which is relative to an offsetParent that
       is not this rail. Early return keeps 'nearest''s one good property —
       a thumb already fully in view does not move the rail at all. */
    function revealThumb(thumb) {
      if (!rail || !thumb) return;
      var railBox = rail.getBoundingClientRect();
      var thumbBox = thumb.getBoundingClientRect();
      var left = rail.scrollLeft + (thumbBox.left - railBox.left) - rail.clientLeft;
      if (left >= rail.scrollLeft && left + thumbBox.width <= rail.scrollLeft + rail.clientWidth) return;
      rail.scrollTo({ left: Math.min(left, railMax()), behavior: reducedMotion() ? 'auto' : 'smooth' });
      // The scroll listener covers the real case; this covers the reduced-
      // motion path, where scrollTo lands before the event is dispatched.
      syncArrows();
    }

    if (mainImg && thumbs.length) {
      buildArrows();
      buildCounter();
      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          updateCounter(thumb);
          var full = thumb.getAttribute('data-full');
          if (!full) return;
          thumbs.forEach(function (t) { t.setAttribute('aria-pressed', 'false'); });
          thumb.setAttribute('aria-pressed', 'true');
          if (full === mainImg.getAttribute('src')) return;
          var alt = thumb.querySelector('img');
          var swap = function () {
            mainImg.setAttribute('src', full);
            mainImg.removeAttribute('srcset');
            if (alt) mainImg.setAttribute('alt', alt.getAttribute('alt') || mainImg.getAttribute('alt'));
          };
          if (window.gsap && !reducedMotion()) {
            window.gsap.to(mainImg, {
              autoAlpha: 0.25, duration: 0.14, ease: 'power1.out',
              onComplete: function () {
                swap();
                window.gsap.to(mainImg, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' });
              }
            });
          } else {
            swap();
          }
        });
      });

      var galPrev = gallery.querySelector('[data-gal-prev]');
      var galNext = gallery.querySelector('[data-gal-next]');
      var galStep = function (dir) {
        var cur = thumbs.findIndex(function (t) { return t.getAttribute('aria-pressed') === 'true'; });
        var next = (cur + dir + thumbs.length) % thumbs.length;
        thumbs[next].click();
        revealThumb(thumbs[next]);
      };
      if (galPrev) galPrev.addEventListener('click', function () { galStep(-1); });
      if (galNext) galNext.addEventListener('click', function () { galStep(1); });
    }

    // Per-item video from the synced description (a bare YouTube URL in
    // lz-specs, surfaced by lauzon-product-story.js). Only used when the
    // product has no native video media.
    document.addEventListener('lauzon:product-video', function (e) {
      if (gallery.querySelector('[data-gal-video-host]') || gallery.querySelector('[data-gal-video]')) return;
      var url = e.detail.url;
      var idMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
      if (!idMatch) return;
      var host = document.createElement('div');
      host.className = 'gal-video-host';
      host.setAttribute('data-gal-video-host', '');
      host.hidden = true;
      host.innerHTML = '<iframe src="https://www.youtube-nocookie.com/embed/' + idMatch[1] + '" title="Product video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen loading="lazy"></iframe>';
      gallery.appendChild(host);

      var main = gallery.querySelector('.gallery-main');
      var watch = document.createElement('button');
      watch.className = 'gal-watch';
      watch.type = 'button';
      watch.setAttribute('data-gal-video', '');
      var label = gallery.dataset.videoLabel || 'Watch this one';
      watch.innerHTML = '<span class="gal-watch-play" aria-hidden="true"></span>';
      watch.appendChild(document.createTextNode(label));
      if (main) main.appendChild(watch);

      // Same play-icon tile the Liquid renders for native video media, so the
      // video is also reachable from the thumbnail strip. First in the strip,
      // not last — Hall of Fame galleries run to 65 photos and a tile at the
      // end would never be seen.
      var strip = gallery.querySelector('.gallery-thumbs');
      if (strip) {
        var tile = document.createElement('button');
        tile.className = 'thumb thumb--video';
        tile.type = 'button';
        tile.setAttribute('data-gal-video', '');
        tile.setAttribute('aria-label', label);
        tile.innerHTML = '<span class="play" aria-hidden="true"></span><span class="video-label" aria-hidden="true">' + label + '</span>';
        strip.insertBefore(tile, strip.firstChild);
        // Scroll anchoring keeps the rail snapped where it was, which hides
        // the tile just inserted ahead of it — pin back to the start.
        strip.scrollLeft = 0;
      }
      wireVideo();
    });

    // Per-item video: swap the main image area for the hosted video player.
    // Works for both a Liquid-rendered host (native video media) and one
    // injected from the synced description URL.
    function wireVideo() {
      var videoHost = gallery.querySelector('[data-gal-video-host]');
      if (!videoHost || videoHost.dataset.wired) return;
      videoHost.dataset.wired = '1';
      document.querySelectorAll('[data-gal-video]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          videoHost.hidden = false;
          gallery.querySelector('.gallery-main').style.display = 'none';
          var vid = videoHost.querySelector('video');
          if (vid) vid.play();
          // Any image thumb click restores the photo view
          thumbs.forEach(function (t) {
            t.addEventListener('click', function restore() {
              videoHost.hidden = true;
              gallery.querySelector('.gallery-main').style.display = '';
              if (vid) vid.pause();
              var frame = videoHost.querySelector('iframe');
              if (frame) frame.src = frame.src; // stop playback
              t.removeEventListener('click', restore);
            });
          });
        });
      });
    }
    wireVideo();

    // Sticky mini add-to-cart: visible once the buy box CTAs scroll above
    // the viewport (plain scroll check; IO can stall in throttled tabs)
    var minibar = document.querySelector('[data-minibar]');
    var buyCtas = document.querySelector('.buybox-ctas');
    if (minibar && buyCtas) {
      // Phones, in-stock pages: also show it while the CTAs are still below
      // the fold, so price and Add to cart are on the first screen. Not on
      // catalogue pages: the bar submits the current finish and payment
      // choice before the buyer has seen them (Dave S., 2026-10-06).
      var mbEarly = !document.querySelector('[data-cx-buybox]');
      var mbPhone = window.matchMedia('(max-width: 749px)');
      var mbUpdate = function () {
        var r = buyCtas.getBoundingClientRect();
        var show = r.bottom < 0 || (mbEarly && mbPhone.matches && r.top > window.innerHeight);
        minibar.classList.toggle('is-visible', show);
        minibar.setAttribute('aria-hidden', show ? 'false' : 'true');
      };
      window.addEventListener('scroll', mbUpdate, { passive: true });
      window.addEventListener('resize', mbUpdate);
      mbUpdate();

      // The bar is position: fixed, so at the bottom of the page it sat on top
      // of the footer's last row — the copyright, the payment chips and the
      // new market picker (Dave S., 2026-09-08). Rather than hiding the bar
      // near the footer, reserve a strip its own height at the very END of the
      // document: the fixed bar then rests over that empty strip, BELOW the
      // footer's last pixel, and covers nothing. No scroll maths, because the
      // strip does the work.
      //
      // The strip is appended to <body> so it lands after the footer whatever
      // the template does, and it is painted --ink so it reads as the footer
      // running on rather than a white band under it.
      var mbSpacer = document.querySelector('[data-minibar-spacer]');
      if (!mbSpacer) {
        mbSpacer = document.createElement('div');
        mbSpacer.setAttribute('data-minibar-spacer', '');
        mbSpacer.className = 'minibar-spacer';
        mbSpacer.setAttribute('aria-hidden', 'true');
        document.body.appendChild(mbSpacer);
      }
      // Measured, not hardcoded: the bar wraps to two rows on a narrow phone
      // and its height changes with the viewport.
      //
      // It has to be measured LATE and again on change. Measured once at init
      // it came back 0 and the strip collapsed — init can run before the
      // stylesheet has laid the bar out, and the same thing happens in any
      // context that starts the page at a zero-height viewport. ResizeObserver
      // is the reliable one; the load and resize listeners cover browsers
      // without it.
      var mbSize = function () {
        var h = minibar.offsetHeight;
        if (h) mbSpacer.style.height = h + 'px';
      };
      window.addEventListener('resize', mbSize);
      window.addEventListener('load', mbSize);
      if (window.ResizeObserver) {
        new ResizeObserver(mbSize).observe(minibar);
      }
      mbSize();

      // The minibar button proxies the real product form submit
      var mbAdd = minibar.querySelector('[data-minibar-add]');
      var realSubmit = document.querySelector('.product-form__submit');
      if (mbAdd && realSubmit) {
        mbAdd.addEventListener('click', function () { realSubmit.click(); });
      }
    }
  }

  init(document);
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
